import React, { memo, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useCartContext } from "context/UseCartContext.js";
import { getUserAccount, pwdReset, CustomergetAll } from "api/CustomerApi.js";
import { generateotp, getTemplateContent, getTemplateDetails, validateOtp } from "api/OtpApi.js";
import { getAllRole } from "api/RoleApi";
import { getBranchId } from "utility/HeaderConfig.js";
import toast from "react-hot-toast";

function ForgetPassword({ onBack,userNo }) {
  // Step Management
  const [step, setStep] = useState(1); // 1: Mobile Entry, 2: OTP Verification, 3: New Password
  
  // Form Data
  const [mobileNo, setMobileNo] = useState(userNo);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // OTP Data
  const [otp, setOtp] = useState("");
  const [otpId, setOtpId] = useState("");
  const [customerInfo, setCustomerInfo] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [templateId, setTemplateId] = useState("");
  
  // Validation States
  const [mobileInvalid, setMobileInvalid] = useState("");
  const [emptyField, setEmptyField] = useState(false);
  const [invalidOTP, setInvalidOTP] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState("");
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [accountExists, setAccountExists] = useState(true);
  
  const { dispatch, state } = useCartContext();

  // Auto-populate mobile number from login form or context
  // useEffect(() => {
  //   // Try to get mobile number from multiple sources
  //   const getMobileFromStorage = () => {
  //     // Option 1: Check if we have stored user data from login
  //     const rememberedUser = localStorage.getItem("rememberedUser");
  //     if (rememberedUser) {
  //       try {
  //         const { mobileNo: rememberedMobile } = JSON.parse(rememberedUser);
  //         if (rememberedMobile) {
  //           setMobileNo(rememberedMobile);
  //           return true;
  //         }
  //       } catch (e) {}
  //     }
      
  //     // Option 2: Check if there's a mobile number in global state
  //     if (state?.userData?.mobile) {
  //       setMobileNo(state.userData.mobile);
  //       return true;
  //     }
      
  //     // Option 3: Check session storage for recently used mobile
  //     const recentMobile = sessionStorage.getItem("recentMobile");
  //     if (recentMobile) {
  //       setMobileNo(recentMobile);
  //       return true;
  //     }
      
  //     return false;
  //   };
    
  //   getMobileFromStorage();
  // }, [state]);

  useEffect(() => {
    getTemplateInfo();
    getRoleInfo();
    //eslint-disable-next-line
  }, []);

  // Resend OTP Timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const getTemplateInfo = () => {
    const filterData = "FORGOT_PASSWORD";
    getTemplateDetails(filterData).then((data) => {
      if (data?.response) {
        setTemplateId(data.response);
        getTemplateContent({ name: filterData }).then((d) => {
          if (d?.response?.content?.[0]?.content) {
            setTemplateContent(d.response.content[0].content);
          }
        });
      }
    }).catch((err) => {
      console.error("Error fetching template:", err);
      // Set default templateId if API fails
      setTemplateId("default_template");
    });
  };

  const getRoleInfo = () => {
    const filterData = { name: "Customer" };
    getAllRole(filterData).then((data) => {
      if (data?.response?.[0]?.id) {
        localStorage.setItem("customerRoleId", data.response[0].id);
      }
    }).catch((err) => {
      console.error("Error fetching role:", err);
    });
  };

  // Step 1: Verify Mobile & Send OTP
  const verifyMobile = async (e) => {
    e.preventDefault();
    setEmptyField(false);
    setMobileInvalid("");

    const mobileReg = /^[0-9]{10}$/;
    
    if (!mobileReg.test(mobileNo)) {
      setMobileInvalid("Please enter a valid 10-digit mobile number");
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    try {
      // Check if customer exists
      const customerData = await CustomergetAll({
        mobile: mobileNo,
        branchDetailsEntity: getBranchId()
      });

      if (customerData?.response && customerData.response.length > 0) {
        const customerId = customerData.response[0]?.id;
        setCustomerInfo(customerId);
        setAccountExists(true);

        // Store the mobile number for future reference
        sessionStorage.setItem("recentMobile", mobileNo);
        if (state?.userData?.mobile) {
          // If we have user context, update it too
          localStorage.setItem("rememberedUser", JSON.stringify({ mobileNo, password: "" }));
        }

        // Generate and send OTP
        const otpPayload = {
          communicationChannel: "SMS",
          message: templateContent || "Your OTP is: {otp}",
          remark: "Password Reset",
          searchData: customerId,
          senderType: "OTP_CUSTOMER",
          templateId: templateId || "default_template"
        };

        const otpResponse = await generateotp(JSON.stringify(otpPayload));
        
        if (otpResponse?.response) {
          setOtpId(otpResponse.response);
          toast.success("OTP sent to your mobile number");
          setStep(2);
          setResendTimer(30); // Start 30-second resend timer
        } else {
          toast.error("Failed to send OTP. Please try again.");
        }
      } else {
        setAccountExists(false);
        setMobileInvalid("No account found for this mobile number. Please register first.");
        toast.error("No account found for this mobile number");
      }
    } catch (error) {
      console.error("Error in mobile verification:", error);
      toast.error("Error verifying mobile number. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    try {
      const otpPayload = {
        communicationChannel: "SMS",
        message: templateContent || "Your OTP is: {otp}",
        remark: "Password Reset - Resend",
        searchData: customerInfo,
        senderType: "OTP_CUSTOMER",
        templateId: templateId || "default_template"
      };

      const otpResponse = await generateotp(JSON.stringify(otpPayload));
      
      if (otpResponse?.response) {
        setOtpId(otpResponse.response);
        setOtp("");
        setInvalidOTP(false);
        toast.success("OTP resent successfully");
        setResendTimer(30);
      } else {
        toast.error("Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      toast.error("Error resending OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const verifyOTP = async (e) => {
    e.preventDefault();
    setEmptyField(false);
    setInvalidOTP(false);

    const otpReg = /^[0-9]{6}$/;
    
    if (!otpReg.test(otp)) {
      setInvalidOTP(true);
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const validationResponse = await validateOtp(otpId, otp);
      
      if (validationResponse?.response === true) {
        toast.success("OTP verified successfully");
        setStep(3);
      } else {
        setInvalidOTP(true);
        toast.error("Invalid OTP. Please enter the correct OTP");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setInvalidOTP(true);
      toast.error("Error verifying OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle password change (only numbers, max 4 digits)
  const handlePasswordChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setPassword(value);
    setEmptyField(false);
    setPasswordInvalid("");
    setPasswordMismatch(false);
  };

  // Handle confirm password change (only numbers, max 4 digits)
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setConfirmPassword(value);
    setPasswordMismatch(false);
  };

  // Step 3: Reset Password
  const resetPassword = async () => {
    setEmptyField(false);
    setPasswordMismatch(false);
    setPasswordInvalid("");

    // Validation
    if (!password) {
      setEmptyField(true);
      toast.error("Please enter a new password");
      return;
    }

    if (password.length !== 4) {
      setPasswordInvalid("Password must be exactly 4 digits");
      toast.error("Password must be exactly 4 digits");
      return;
    }

    if (password !== confirmPassword) {
      setPasswordMismatch(true);
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const rolesList = localStorage.getItem("customerRoleId");
      
      const resetPayload = {
        id: customerInfo,
        username: mobileNo,
        password: password,
        rolesList: rolesList ? [rolesList] : []
      };

      await pwdReset(resetPayload);
      
      toast.success("Password reset successfully!", {
        duration: 2000,
        position: "top-right",
      });
      
      // Clear stored password from remembered user
      const rememberedUser = localStorage.getItem("rememberedUser");
      if (rememberedUser) {
        try {
          const userData = JSON.parse(rememberedUser);
          if (userData.mobileNo === mobileNo) {
            // Update stored user with new password (or clear it so user enters fresh)
            localStorage.setItem("rememberedUser", JSON.stringify({ 
              mobileNo: userData.mobileNo, 
              password: password 
            }));
          }
        } catch (e) {}
      }
      
      // Redirect to login after success
      setTimeout(() => {
        if (onBack) onBack(); else dispatch({ type: "MODAL", payload: "login" });
      }, 1500);
      
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setOtp(value);
      setInvalidOTP(false);
    }
  };

  // Handle OTP key press (auto-submit on Enter when 6 digits)
  const handleOtpKeyPress = (e) => {
    if (e.key === "Enter" && otp.length === 6) {
      verifyOTP(e);
    }
  };

  // Mobile number input handler
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNo(value);
    setMobileInvalid("");
    setAccountExists(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -100, opacity: 0 }}
        style={{ width: "100%" }}
      >
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ 
            fontSize: 11, fontWeight: 700, letterSpacing: ".14em", 
            textTransform: "uppercase", color: "#b8860b", margin: "0 0 6px" 
          }}>
            Account Recovery
          </p>
          <h2 style={{ 
            fontFamily: "var(--font-display,Georgia,serif)", fontSize: 28, 
            fontWeight: 500, color: "#1c1c1e", margin: "0 0 4px" 
          }}>
            {step === 1 ? "Forgot Password" : step === 2 ? "Verify OTP" : "Reset Password"}
          </h2>
          <p style={{ fontSize: 13, color: "#98989d", margin: 0 }}>
            {step === 1 
              ? "Enter your registered mobile number to receive OTP" 
              : step === 2 
                ? `Enter the 6-digit code sent to +91 ${mobileNo}`
                : "Create a new 4-digit numeric password for your account"
            }
          </p>
        </div>

        {/* Step Progress Indicator */}
        <div style={{ 
          display: "flex", gap: 6, marginBottom: 24 
        }}>
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              style={{ 
                flex: 1, height: 3, borderRadius: 99,
                background: s <= step ? "#b8860b" : "#e5e5ea",
                transition: "background 0.3s ease"
              }} 
            />
          ))}
        </div>

        <form onSubmit={(e) => e.preventDefault()}>
          {/* Step 1: Mobile Number */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div style={{ marginBottom: 20 }}>
                <label style={fpLabelStyle}>Mobile Number</label>
                <div style={{ position: "relative" }}>
                  <span style={fpPrefixStyle}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.95 9.91a19.79 19.79 0 01-3.07-8.67A2 2 0 012.88 1h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L7.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.34 1.85.574 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                  </span>
                  <input
                    type="tel"
                    pattern="[0-9]{10}"
                    autoComplete="off"
                    value={mobileNo}
                    onChange={handleMobileChange}
                    maxLength={10}
                    style={{ 
                      ...fpInputStyle, 
                      paddingLeft: 42,
                      borderColor: (emptyField && mobileNo === "") || mobileInvalid ? "#e53e3e" : "#e5e5ea"
                    }}
                    placeholder="Enter registered mobile number"
                    disabled={loading}
                  />
                </div>
                {emptyField && !mobileNo && (
                  <span style={fpErrStyle}>Mobile number is required</span>
                )}
                {mobileInvalid && (
                  <span style={fpErrStyle}>{mobileInvalid}</span>
                )}
              </div>

              <button 
                type="button" 
                style={{
                  ...fpBtnStyle,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading || mobileNo.length !== 10 ? "not-allowed" : "pointer",
                  background: mobileNo.length === 10 && !loading ? "#1c1c1e" : "#ccc"
                }}
                onClick={verifyMobile}
                disabled={loading || mobileNo.length !== 10}
              >
                {loading ? (
                  <>
                    <span style={{ 
                      width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", 
                      borderTopColor: "#fff", borderRadius: "50%", 
                      animation: "fpSpin 0.6s linear infinite",
                      marginRight: 8
                    }} />
                    Sending OTP...
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>

              <p style={{ fontSize: 13, color: "#98989d", textAlign: "center", marginTop: 16 }}>
                Remember your password?{" "}
                <button 
                  type="button"
                  style={{ ...fpLinkStyle }}
                  onClick={() => { if (onBack) onBack(); else dispatch({ type: "MODAL", payload: "login" }); }}
                >
                  Sign In
                </button>
              </p>
            </motion.div>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div style={{ marginBottom: 20 }}>
                <label style={fpLabelStyle}>OTP Code</label>
                <div>
                  <input
                    type="text"
                    pattern="[0-9]{6}"
                    inputMode="numeric"
                    autoComplete="off"
                    value={otp}
                    onChange={handleOtpChange}
                    onKeyPress={handleOtpKeyPress}
                    style={{ 
                      ...fpInputStyle, 
                      textAlign: "center", 
                      letterSpacing: "0.5em", 
                      fontSize: 22, 
                      fontWeight: 700,
                      padding: "16px 14px",
                      borderColor: invalidOTP ? "#e53e3e" : "#e5e5ea"
                    }}
                    placeholder="• • • • • •"
                    maxLength={6}
                    disabled={loading}
                  />
                  {/* OTP Dots Indicator */}
                  <div style={{ 
                    display: "flex", gap: 8, justifyContent: "center", marginTop: 12 
                  }}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div 
                        key={i} 
                        style={{ 
                          width: 10, height: 10, borderRadius: "50%", 
                          background: i < otp.length ? "#b8860b" : "rgba(184,134,11,0.2)", 
                          transition: "background 0.2s ease" 
                        }} 
                      />
                    ))}
                  </div>
                </div>
                {invalidOTP && (
                  <span style={fpErrStyle}>Invalid OTP. Please try again.</span>
                )}
              </div>

              <button 
                type="button" 
                style={{
                  ...fpBtnStyle,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading || otp.length !== 6 ? "not-allowed" : "pointer",
                  background: otp.length === 6 && !loading ? "#1c1c1e" : "#ccc"
                }}
                onClick={verifyOTP}
                disabled={loading || otp.length !== 6}
              >
                {loading ? (
                  <>
                    <span style={{ 
                      width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", 
                      borderTopColor: "#fff", borderRadius: "50%", 
                      animation: "fpSpin 0.6s linear infinite",
                      marginRight: 8
                    }} />
                    Verifying...
                  </>
                ) : (
                  "Verify & Continue"
                )}
              </button>

              <div style={{ 
                display: "flex", justifyContent: "space-between", 
                alignItems: "center", marginTop: 16 
              }}>
                <button 
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setOtp("");
                    setInvalidOTP(false);
                  }}
                  style={{ ...fpLinkStyle, fontSize: 13 }}
                >
                  ← Change Number
                </button>
                <button 
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || loading}
                  style={{ 
                    ...fpLinkStyle, 
                    fontSize: 13,
                    opacity: resendTimer > 0 ? 0.5 : 1,
                    cursor: resendTimer > 0 ? "not-allowed" : "pointer"
                  }}
                >
                  {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* New Password */}
              <div style={{ marginBottom: 16 }}>
                <label style={fpLabelStyle}>New Password (4-digit numeric)</label>
                <div style={{ position: "relative" }}>
                  <span style={fpPrefixStyle}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    style={{ 
                      ...fpInputStyle, 
                      paddingLeft: 42, 
                      paddingRight: 42,
                      borderColor: (emptyField && password === "") || passwordInvalid ? "#e53e3e" : "#e5e5ea"
                    }}
                    placeholder="Enter 4-digit numeric password"
                    value={password}
                    onChange={handlePasswordChange}
                    maxLength={4}
                    disabled={loading}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    style={{ 
                      position: "absolute", right: 12, top: "50%", 
                      transform: "translateY(-50%)", background: "none", 
                      border: "none", cursor: "pointer", color: "#98989d", 
                      display: "flex", padding: 4
                    }}
                  >
                    {showPassword ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {emptyField && !password && (
                  <span style={fpErrStyle}>Password is required</span>
                )}
                {passwordInvalid && (
                  <span style={fpErrStyle}>{passwordInvalid}</span>
                )}
                {password && password.length > 0 && password.length < 4 && (
                  <span style={fpErrStyle}>Password must be 4 digits (current: {password.length}/4)</span>
                )}
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: 20 }}>
                <label style={fpLabelStyle}>Confirm Password</label>
                <div style={{ position: "relative" }}>
                  <span style={fpPrefixStyle}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </span>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    style={{ 
                      ...fpInputStyle, 
                      paddingLeft: 42, 
                      paddingRight: 42,
                      borderColor: passwordMismatch ? "#e53e3e" : "#e5e5ea"
                    }}
                    placeholder="Confirm 4-digit password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    maxLength={4}
                    disabled={loading}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                    style={{ 
                      position: "absolute", right: 12, top: "50%", 
                      transform: "translateY(-50%)", background: "none", 
                      border: "none", cursor: "pointer", color: "#98989d", 
                      display: "flex", padding: 4
                    }}
                  >
                    {showConfirmPassword ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {passwordMismatch && (
                  <span style={fpErrStyle}>Passwords do not match</span>
                )}
              </div>

              <button 
                type="button" 
                style={{
                  ...fpBtnStyle,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading || password.length !== 4 || confirmPassword.length !== 4 ? "not-allowed" : "pointer",
                  background: password.length === 4 && confirmPassword.length === 4 && !loading ? "#1c1c1e" : "#ccc"
                }}
                onClick={resetPassword}
                disabled={loading || password.length !== 4 || confirmPassword.length !== 4}
              >
                {loading ? (
                  <>
                    <span style={{ 
                      width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", 
                      borderTopColor: "#fff", borderRadius: "50%", 
                      animation: "fpSpin 0.6s linear infinite",
                      marginRight: 8
                    }} />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>

              <p style={{ fontSize: 13, color: "#98989d", textAlign: "center", marginTop: 16 }}>
                <button 
                  type="button"
                  style={{ ...fpLinkStyle }}
                  onClick={() => { if (onBack) onBack(); else dispatch({ type: "MODAL", payload: "login" }); }}
                >
                  Back to Sign In
                </button>
              </p>
            </motion.div>
          )}
        </form>
      </motion.div>

      <style>{`
        @keyframes fpSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </AnimatePresence>
  );
}

// Style constants matching LoginModal
const fpLabelStyle = { 
  display: "block", fontSize: 11, fontWeight: 600, color: "#6c6c70", 
  letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 6 
};
const fpPrefixStyle = { 
  position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", 
  color: "#98989d", pointerEvents: "none", display: "flex", alignItems: "center" 
};
const fpInputStyle = { 
  display: "block", width: "100%", padding: "12px 14px", fontFamily: "inherit", 
  fontSize: 14, color: "#1c1c1e", background: "#fafafa", 
  border: "1.5px solid #e5e5ea", borderRadius: 8, outline: "none", 
  transition: "border-color .14s, box-shadow .14s" 
};
const fpErrStyle = { 
  display: "block", fontSize: 11.5, color: "#e53e3e", marginTop: 5 
};
const fpBtnStyle = { 
  display: "flex", alignItems: "center", justifyContent: "center", 
  width: "100%", padding: 13, background: "#1c1c1e", color: "#fff", 
  fontSize: 14, fontWeight: 600, letterSpacing: ".05em", border: "none", 
  borderRadius: 8, cursor: "pointer", transition: "all .14s" 
};
const fpLinkStyle = {
  color: "#b8860b", fontWeight: 600, background: "none", border: "none", 
  padding: 0, cursor: "pointer", textDecoration: "underline", fontSize: "inherit"
};

export default memo(ForgetPassword);